import { Color, Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/24px';
import * as Dialog from '@radix-ui/react-dialog';
import { Children, forwardRef, isValidElement, ReactElement } from 'react';
import styled from 'styled-components';

import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';

export const ModalTrigger = Dialog.Trigger;
export const ModalPortal = Dialog.Portal;
export const ModalContent = styled(Dialog.Content)`
  overflow-y: auto;
  z-index: 10001;
`;

const ModalOverlay = styled(Dialog.Overlay)`
  @keyframes overlayShow {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  background-color: rgba(0, 0, 0, 0.8);
  position: fixed;
  inset: 0;
  animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 10000;
`;

const ModalCloseButton = styled(Dialog.Close)`
  position: absolute;
  top: ${Spacing[3]};
  right: ${Spacing[3]};
  background: transparent;
  border: 0;
  cursor: pointer;
  color: ${Color.BorderIconSecondary};
`;

export const ModalClose = (props: Dialog.DialogCloseProps) => {
  return (
    <ModalCloseButton {...props}>
      <XIcon />
    </ModalCloseButton>
  );
};

export const ModalTitle = ({ children, ...props }: Dialog.DialogTitleProps) => {
  return (
    <Dialog.Title asChild {...props}>
      <Paragraph as='h3' size='lg' weight='bold' color='primary'>
        {children}
      </Paragraph>
    </Dialog.Title>
  );
};

const StyledCard = styled(Card)<{ $size?: 'auto' | 'md' | 'lg' | 'xl' }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ $size }) => ($size === 'auto' ? 'auto' : '90vw')};
  max-width: ${({ theme, $size }) => {
    if ($size === 'auto') {
      return 'auto';
    }

    return theme.modal.width[$size];
  }};
  /* TODO: Use breakpoints to make this responsive */
  max-height: 85vh;
`;

export interface ModalCardProps extends Dialog.DialogContentProps {
  /**
   * Display X button to close popover
   */
  showCloseButton?: boolean;

  /**
   * Remove padding from Card
   */
  noPadding?: boolean;

  /**
   * Max width of the modal
   */
  size?: 'auto' | 'md' | 'lg' | 'xl';
}

/**
 * Card to be used in Modal contexts. Inherits props from Radix UI Modal.Content.
 */
export const ModalCard = forwardRef<React.ElementRef<typeof Dialog.DialogContent>, ModalCardProps>(
  function PopoverCard(
    { title, children, showCloseButton = true, noPadding, size, ...props },
    ref,
  ) {
    const modalTitle = title ? <ModalTitle>{title}</ModalTitle> : null;
    return (
      <>
        <ModalOverlay />
        <ModalContent asChild {...props} ref={ref}>
          <StyledCard elevated $size={size}>
            {showCloseButton && <ModalClose aria-label='Close' />}
            {noPadding ? (
              <>
                {modalTitle}
                {children}
              </>
            ) : (
              <Card.Body>
                {modalTitle}
                {children}
              </Card.Body>
            )}
          </StyledCard>
        </ModalContent>
      </>
    );
  },
);

export interface ModalProps extends Dialog.DialogProps {
  /**
   * Title of the Card
   */
  title?: string;

  /**
   * Component to use as trigger. Radix UI trigger component props will be merged with this element.
   */
  trigger?: ReactElement;

  /**
   * Props to apply to Popover.Trigger within
   */
  triggerProps?: Dialog.DialogTriggerProps;

  /**
   * Props to apply to Popover.Card within
   */
  contentProps?: Dialog.DialogContentProps;

  /**
   * Render content within a portal
   */
  portal?: boolean;

  /**
   * Props applied to Radix UI Portal component
   */
  portalProps?: Dialog.DialogPortalProps;

  /**
   * Max width of the modal
   */
  size?: 'auto' | 'md' | 'lg' | 'xl';
}

const Modal = ({
  children,
  trigger,
  triggerProps,
  contentProps,
  portal = true,
  portalProps,
  size = 'md',
  title,
  ...props
}: ModalProps) => {
  let triggerElem;
  let contentElem;

  const renderChildren = Children.toArray(children).filter((child) => {
    if (isValidElement(child)) {
      switch (child.type) {
        case Dialog.Trigger:
          triggerElem = child;
          return false;
        case ModalCard:
        case Dialog.Content:
          contentElem = child;
          break;
      }
    }

    return true;
  });

  const content = contentElem || (
    <ModalCard size={size} title={title} {...contentProps}>
      {renderChildren}
    </ModalCard>
  );

  return (
    <Dialog.Root {...props}>
      {triggerElem ||
        (trigger && (
          <ModalTrigger asChild {...triggerProps}>
            {trigger}
          </ModalTrigger>
        ))}
      {portal ? <ModalPortal {...portalProps}>{content}</ModalPortal> : content}
    </Dialog.Root>
  );
};

export default Object.assign(Modal, {
  Title: ModalTitle,
  Trigger: ModalTrigger,
  Portal: ModalPortal,
  Content: ModalContent,
  Card: ModalCard,
  Close: ModalClose,
});
